import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, EventEmitter, input, Output, signal } from '@angular/core';
import { FireflyListModule } from '../../../lists/list.module';
import { SelectionType } from '../../models/selection-type.enum';
import { FireflyLocationEditableChipGroupComponent } from '../location-editable-chip-group/location-editable-chip-group.component';

/**
 * `f-location-chip-group`
 *
 * Encapsulates the collapsed chip group for the location suggester, including:
 *  - The location-specific popover template (name + include/exclude label)
 *  - The mobile modal pending-items buffer (`_modalPendingItems`)
 *
 * Wraps `f-editable-chip-group` and wires the `customPopoverTpl`,
 * `modalOpened` and `modalClosed` bindings internally so that
 * `f-location-suggester` stays free of mobile-modal bookkeeping.
 */
@Component({
  selector: 'f-location-chip-group',
  standalone: true,
  imports: [CommonModule, FireflyLocationEditableChipGroupComponent, FireflyListModule],
  templateUrl: './location-chip-group.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FireflyLocationChipGroupComponent {
  /** Selected chip items shaped for `f-editable-chip-group` (must carry `key`, `name`, `selectionType`, `selectionLabel`). */
  readonly items = input<Record<string, unknown>[]>([]);

  /** Excluded items — forwarded as `invalidItems` to apply danger styling. */
  readonly invalidItems = input<Record<string, unknown>[]>([]);

  /** Emits when a chip is removed (passed through from the editable chip group or resolved from the modal buffer). */
  @Output() readonly remove = new EventEmitter<Record<string, unknown>>();

  /** Emits when the user clears all chips. */
  @Output() readonly removeAll = new EventEmitter<void>();

  /**
   * Emits when the user clicks a chip's label text inside the modal.
   * The parent (`f-location-suggester`) uses this to toggle Include ↔ Exclude.
   */
  @Output() readonly itemClick = new EventEmitter<Record<string, unknown>>();

  // ─── Expose enums to template ─────────────────────────────────────────────
  readonly SelectionType = SelectionType;

  // ─── Mobile modal pending-items buffer ────────────────────────────────────
  /**
   * Snapshot taken when the mobile chip-group modal opens.
   * `null`  → modal is closed; operations apply immediately.
   * `array` → modal is open;  removals accumulate here until confirmed/dismissed.
   */
  private readonly _modalPendingItems = signal<Record<string, unknown>[] | null>(null);

  /**
   * Items rendered inside the mobile modal list.
   * While the modal is open this shows the pending snapshot (so removals are
   * reflected immediately in the UI without mutating the actual form value).
   * When no modal is open it falls back to the live `items()` input.
   */
  readonly modalDisplayItems = computed(() => this._modalPendingItems() ?? this.items());

  // ─── Handlers wired to f-editable-chip-group outputs ─────────────────────

  /** Called by `(modalOpened)` — snapshot the current chip list into the buffer. */
  onModalOpened(): void {
    this._modalPendingItems.set([...this.items()]);
  }

  /**
   * Called by `(modalClosed)`.
   * On confirm  → diff pending vs current and emit `remove` for each deleted chip.
   * On dismiss  → discard the buffer (the chip group already reverted its visual state).
   */
  onModalClosed(confirmed: boolean): void {
    if (confirmed) {
      const pending = this._modalPendingItems();
      if (pending !== null) {
        const pendingKeys = new Set(pending.map(p => p['key'] as string));
        this.items()
          .filter(c => !pendingKeys.has(c['key'] as string))
          .forEach(item => this.remove.emit(item));
      }
    }
    this._modalPendingItems.set(null);
  }

  /** Called when a chip is removed inside the open modal — buffers the removal. */
  onChipRemoveInModal(item: Record<string, unknown>): void {
    if (this._modalPendingItems() !== null) {
      this._modalPendingItems.update(items => (items ? items.filter(i => i['key'] !== item['key']) : items));
      return;
    }
    // Fallback: modal is not tracked — apply immediately.
    this.remove.emit(item);
  }

  /** Forwards a non-modal chip removal straight to the `remove` output. */
  onChipRemove(item: Record<string, unknown>): void {
    this.remove.emit(item);
  }

  /** Forwards a chip label click to the `itemClick` output (parent toggles Include ↔ Exclude). */
  onChipItemClick(item: Record<string, unknown>): void {
    this.itemClick.emit(item);
  }

  /** Forwards a remove-all action to the `removeAll` output. */
  onRemoveAll(): void {
    this.removeAll.emit();
  }
}
